/**
 * Performance API Service
 * API calls for employee performance tracking
 */

import { api } from './api';
import type {
  ApiResponse,
  PerformanceData,
  PerformanceTrendPoint,
  TeamPerformanceSummary,
  DepartmentPerformanceSummary,
  OrganizationPerformanceSummary,
  PerformanceWeights,
  PerformanceQueryParams,
  PerformanceExportParams,
} from '../types';

// ============================================
// QUERY PARAM HELPERS
// ============================================

function buildQueryString(params: PerformanceQueryParams): string {
  const searchParams = new URLSearchParams();

  if (params.periodType) {
    searchParams.append('periodType', params.periodType);
  }
  if (params.startDate) {
    searchParams.append('startDate', params.startDate);
  }
  if (params.endDate) {
    searchParams.append('endDate', params.endDate);
  }
  if (params.trendPeriods) {
    searchParams.append('trendPeriods', params.trendPeriods.toString());
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

// ============================================
// INDIVIDUAL PERFORMANCE (ALL ROLES - OWN DATA)
// ============================================

/**
 * Get current user's performance
 */
export async function getMyPerformance(
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<PerformanceData>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<PerformanceData>>(`/performance/me${queryString}`);
  return response.data;
}

/**
 * Get current user's OKR performance breakdown
 */
export async function getMyOKRPerformance(
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<{ okrs: PerformanceData['okrs']; okrProgressScore: number }>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<{ okrs: PerformanceData['okrs']; okrProgressScore: number }>>(
    `/performance/me/okrs${queryString}`
  );
  return response.data;
}

/**
 * Get current user's performance trend
 */
export async function getMyTrend(
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<PerformanceTrendPoint[]>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<PerformanceTrendPoint[]>>(
    `/performance/me/trend${queryString}`
  );
  return response.data;
}

// ============================================
// TEAM PERFORMANCE (MANAGERS AND ADMINS)
// ============================================

/**
 * Get team performance summary
 */
export async function getTeamPerformance(
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<TeamPerformanceSummary>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<TeamPerformanceSummary>>(
    `/performance/team${queryString}`
  );
  return response.data;
}

/**
 * Get specific direct report's performance
 */
export async function getDirectReportPerformance(
  userId: string,
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<PerformanceData>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<PerformanceData>>(
    `/performance/team/${userId}${queryString}`
  );
  return response.data;
}

/**
 * Get specific direct report's OKR breakdown
 */
export async function getDirectReportOKRs(
  userId: string,
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<{ okrs: PerformanceData['okrs']; okrProgressScore: number }>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<{ okrs: PerformanceData['okrs']; okrProgressScore: number }>>(
    `/performance/team/${userId}/okrs${queryString}`
  );
  return response.data;
}

// ============================================
// ORGANIZATION PERFORMANCE (ADMIN ONLY)
// ============================================

/**
 * Get organization-wide performance summary
 */
export async function getOrganizationPerformance(
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<OrganizationPerformanceSummary>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<OrganizationPerformanceSummary>>(
    `/performance/organization${queryString}`
  );
  return response.data;
}

/**
 * Get all departments' performance summary
 */
export async function getDepartmentsPerformance(
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<DepartmentPerformanceSummary[]>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<DepartmentPerformanceSummary[]>>(
    `/performance/organization/departments${queryString}`
  );
  return response.data;
}

/**
 * Get all managers' team performance summary
 */
export async function getManagersPerformance(
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<TeamPerformanceSummary['directReports']>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<TeamPerformanceSummary['directReports']>>(
    `/performance/organization/managers${queryString}`
  );
  return response.data;
}

/**
 * Get specific department's performance
 */
export async function getDepartmentPerformance(
  departmentId: string,
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<DepartmentPerformanceSummary>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<DepartmentPerformanceSummary>>(
    `/performance/department/${departmentId}${queryString}`
  );
  return response.data;
}

/**
 * Get any user's performance (admin only)
 */
export async function getUserPerformance(
  userId: string,
  params: PerformanceQueryParams = {}
): Promise<ApiResponse<PerformanceData>> {
  const queryString = buildQueryString(params);
  const response = await api.get<ApiResponse<PerformanceData>>(
    `/performance/user/${userId}${queryString}`
  );
  return response.data;
}

// ============================================
// CONFIGURATION
// ============================================

/**
 * Get current performance weights
 */
export async function getPerformanceWeights(): Promise<ApiResponse<PerformanceWeights>> {
  const response = await api.get<ApiResponse<PerformanceWeights>>('/performance/settings/weights');
  return response.data;
}

/**
 * Update performance weights (admin only)
 */
export async function updatePerformanceWeights(
  weights: PerformanceWeights
): Promise<ApiResponse<PerformanceWeights>> {
  const response = await api.put<ApiResponse<PerformanceWeights>>(
    '/performance/settings/weights',
    weights
  );
  return response.data;
}

// ============================================
// EXPORT
// ============================================

/**
 * Get performance data for PDF export
 */
export async function getPerformanceExportData(
  params: PerformanceExportParams
): Promise<ApiResponse<PerformanceData | TeamPerformanceSummary | DepartmentPerformanceSummary | OrganizationPerformanceSummary>> {
  const response = await api.post<ApiResponse<PerformanceData | TeamPerformanceSummary | DepartmentPerformanceSummary | OrganizationPerformanceSummary>>(
    '/performance/export/pdf',
    params
  );
  return response.data;
}

// ============================================
// CONVENIENCE METHODS
// ============================================

/**
 * Get performance for the current quarter
 */
export async function getMyCurrentQuarterPerformance(): Promise<ApiResponse<PerformanceData>> {
  return getMyPerformance({ periodType: 'quarterly' });
}

/**
 * Get team performance for the current quarter
 */
export async function getTeamCurrentQuarterPerformance(): Promise<ApiResponse<TeamPerformanceSummary>> {
  return getTeamPerformance({ periodType: 'quarterly' });
}

/**
 * Get organization performance for the current quarter
 */
export async function getOrgCurrentQuarterPerformance(): Promise<ApiResponse<OrganizationPerformanceSummary>> {
  return getOrganizationPerformance({ periodType: 'quarterly' });
}

// Export all as a namespace-like object for easier imports
export const performanceApi = {
  // Individual
  getMyPerformance,
  getMyOKRPerformance,
  getMyTrend,

  // Team
  getTeamPerformance,
  getDirectReportPerformance,
  getDirectReportOKRs,

  // Organization
  getOrganizationPerformance,
  getDepartmentsPerformance,
  getManagersPerformance,
  getDepartmentPerformance,
  getUserPerformance,

  // Configuration
  getPerformanceWeights,
  updatePerformanceWeights,

  // Export
  getPerformanceExportData,

  // Convenience
  getMyCurrentQuarterPerformance,
  getTeamCurrentQuarterPerformance,
  getOrgCurrentQuarterPerformance,
};

export default performanceApi;
